<template>
  <BaseModal :modalTitle="$t('W: Ny lukkedag')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :placeholder="$t('W: f.eks. Grundlovsdag')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />
      <UiInputDatePickerVee
        name="date"
        :label="$t('W: Dato')"
        :rules="['required']"
        isRequired
        showAsRequired
        :dateFormat="(date: Date) => $d(date, 'shortMonthDate')"
        v-model="formDateAsDate" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useModalStore } from '@/stores/modalStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { parseDateStr, formatDateToStr } from '@/globals/typescript/utils/dates'

export default {
  name: 'CreateClosedDayModal',

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        date: '',
        name: '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    // The picker works in `Date`, the API and the model in a 'YYYY-MM-DD'
    // business date. Same bridge as AddLabEquipmentMaintenanceModal — the
    // conversion stays local, so the date never shifts across a timezone.
    formDateAsDate: {
      get(): Date | null {
        return parseDateStr(this.form.date)
      },
      set(value: Date | null) {
        this.form.date = value ? formatDateToStr(value) : ''
      },
    },
  },

  methods: {
    ...mapActions(useClosedDayStore, ['createClosedDay']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        await this.createClosedDay({
          date: this.form.date,
          name: this.form.name,
        })
        useToast().success(this.$t('W: Lukkedag oprettet'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.closeTop()
      } catch (error) {
        // The backend answers a date that is already registered with
        // ALREADY_EXISTS rather than throwing — surfaced inline, since it is
        // something the user can fix in the form rather than a failure.
        const isDuplicate =
          error instanceof Error && error.message === 'already-exists'
        const message = isDuplicate
          ? this.$t('W: Datoen er allerede registreret som lukkedag')
          : this.$t('error.common.standard')

        useToast().error(
          isDuplicate ? message : this.$t('W: Kunne ikke oprette lukkedagen')
        )
        this.errorText = message
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
